import { ClaimRecord } from '@typesApp/ClaimRecord'
import { UpdateProductArgs } from '@typesApp/CustomerApi'
import { CartBundle } from '@typesApp/Product'
import { shouldRefreshCartTotals } from '@utils/claimUtil'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { logger } from '@utils/logger'
import { isProductUpdateRequired } from './claimRecordUpdateUtil'

export type CartProductUpdateParams = {
  customerNumber: string | undefined
  ecommerceCode: string | null
  ecommerceTransactionHost?: string
  channel?: string
}

export type CartProductUpdateService = {
  fetchCartBundle: () => Promise<CartBundle>
  updateProductsMutation: (args: UpdateProductArgs) => Promise<void>
  updateRefreshCartFlagMutation: (claimRecordId: string) => Promise<void>
  refreshCart: () => Promise<void>
}

export type CartProductUpdateController = {
  performUpdateProducts: (claimRecord: ClaimRecord) => Promise<void>
}

export const cartProductUpdateController = (
  service: CartProductUpdateService,
  params: CartProductUpdateParams
): CartProductUpdateController => {
  const { customerNumber = '' } = params

  const performUpdateProducts = async (claimRecord: ClaimRecord) => {
    if (!customerNumber) {
      return
    }

    if (isProductUpdateRequired(claimRecord?.cartStatus)) {
      await fetchCartBundleAndUpdateProducts()
    }

    await refreshCartIfRequired(claimRecord)
  }

  const fetchCartBundleAndUpdateProducts = async (): Promise<void> => {
    const ecommerce = getCommerceCode()
    const cartBundle = await service.fetchCartBundle()
    const { products, orderSubtotal, shippingCosts, cartId, channel } = cartBundle ?? {}

    await service.updateProductsMutation({
      customerNumber,
      ecommerce,
      products,
      orderSubtotal,
      shippingCosts,
      cartId,
      channel: channel || params?.channel,
    })
  }

  const getCommerceCode = (): string => {
    const { ecommerceCode, ecommerceTransactionHost } = params ?? {}

    return formatCommerceCode({
      ecommerceCode,
      ecommerceTransactionHost,
    })
  }

  const refreshCartIfRequired = async (claimRecord: ClaimRecord) => {
    if (shouldRefreshCartTotals(claimRecord)) {
      await safeUpdateRefreshCartFlag(claimRecord.id)
    }

    await safeRefreshCart()
  }

  const safeUpdateRefreshCartFlag = async (claimRecordId: string): Promise<void> => {
    try {
      await service.updateRefreshCartFlagMutation(claimRecordId)
    } catch (error) {
      logger.warn('directbilling - update refresh cart flag error', error)
    }
  }

  const safeRefreshCart = async (): Promise<void> => {
    try {
      await service.refreshCart()
    } catch (error) {
      logger.warn('directbilling - refresh cart error', error)
    }
  }

  return {
    performUpdateProducts,
  }
}
